import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { confirmPasswordReset } from 'firebase/auth';
import { auth } from '../../firebase/firebase';
import { Alert, Box, Button, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getHomepageRuntimeConfig } from '../../sitePublishing/publicConfigSource';
import { useOptionalTenant } from '../../tenancy/TenantContext';
import AuthShell, { useBusinessLogoUrl } from '../../components/auth/AuthShell';

const ResetPasswordPage: React.FC = () => {
    const tenantId = useOptionalTenant()?.tenantId;
    const [searchParams] = useSearchParams();
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    const oobCode = searchParams.get('oobCode');
    const signInPath = tenantId ? `/admin?tenant=${encodeURIComponent(tenantId)}` : '/admin';

    const businessConfigQuery = useQuery({
        queryKey: ['publicBusinessConfig', tenantId],
        queryFn: () => getHomepageRuntimeConfig(tenantId!),
        enabled: Boolean(tenantId),
    });
    const businessName = businessConfigQuery.data?.businessInfo?.name?.trim() || '';
    const logoUrl = useBusinessLogoUrl(businessConfigQuery.data?.businessInfo?.logoImagePath);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        try {
            await confirmPasswordReset(auth, oobCode!, newPassword);
            setError(null);
            setSuccess(true);
            setTimeout(() => navigate(signInPath), 3000); // Redirect after success
        } catch (err) {
            console.error(err);
            setError('Invalid or expired reset link');
        }
    };

    if (!oobCode) {
        return (
            <AuthShell logoUrl={logoUrl} businessName={businessName} title="Reset link missing"
                subtitle="Open the link from your reset email again, or request a new one from the sign-in page.">
                <Button href={signInPath} variant="outlined" fullWidth size="large">Go to sign in</Button>
            </AuthShell>
        );
    }

    return (
        <AuthShell logoUrl={logoUrl} businessName={businessName} title="Choose a new password"
            subtitle="Enter it twice. You'll use it the next time you sign in.">
            {success ? (
                <Alert severity="success">Password reset. Taking you back to sign in…</Alert>
            ) : (
                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField
                        label="New password"
                        type="password"
                        autoComplete="new-password"
                        required
                        fullWidth
                        helperText="At least 6 characters."
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <TextField
                        label="Confirm password"
                        type="password"
                        autoComplete="new-password"
                        required
                        fullWidth
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <Button variant="contained" type="submit" size="large" fullWidth
                        sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}>
                        Reset password
                    </Button>
                </Box>
            )}
        </AuthShell>
    );
};

export default ResetPasswordPage;
